import { message } from "antd";
import i18n from "i18next";
import { ZoomMeetingStatus } from "@/types/common";
import { isFocusEnv, FocusSDK, JoyMeetingStatusEnum } from "@/utils";
import bus from "@/utils/bus";
import { createMeeting } from "@/api/meeting";
import cache from "@/utils/cache";
import { AVSDK, BasicUserInfo, SessionStateEnum } from "@/baseComponents/Meeting/common";
import ImService from "@/server/ImService";
import { handleMeetingSendMessage } from "@/baseComponents/Meeting/utils";
import { syncWarn, syncConfirmNew } from "@/utils/syncConfirm";

export const ZOOM_PASS_LENGTH = 6;

let currentCloudUnionMeetingInfo: any = null;

export function getCurrentCloudUnionMeetingInfo() {
  return currentCloudUnionMeetingInfo;
}
export function setCurrentCloudUnionMeetingInfo(meetingInfo: any) {
  currentCloudUnionMeetingInfo = meetingInfo;
}

export async function handleCreateMeeting(
  toUsers: {
    appId: string;
    userId: string;
    teamId: string;
    headImg: string;
    userName: string;
  }[],
  meetingTopic: string
) {
  if (!cache.zoomLogin) {
    const { success, errMsg } = await prepareZoom();
    if (!success) {
      return Promise.reject(new Error(errMsg || "会议初始化失败"));
    }
  }
  const imService = ImService.getInstance();
  const currentEmployee = await imService.getCurrentEmployee();

  const [result, err] = await createMeeting({
    startTime: (new Date().getTime() + 5000).toString(),
    duration: 2,
    topic: meetingTopic,
    password: "",
    participantIds: toUsers.map((item) => {
      return { ...item, realName: item.userName };
    })
  });
  if (err || !result) {
    console.error(err);
    return Promise.reject(new Error(err || "创建会议失败"));
  }
  await joinMeeting({
    password: result.password,
    meetingId: result.meetingId,
    realName: currentEmployee.name || ""
  });
  await handleMeetingSendMessage({
    avsdk: AVSDK.ZOOM,
    sessionState: SessionStateEnum.Call,
    toUsers: toUsers.map((i) => ({
      pin: i.userId,
      teamId: i.teamId,
      app: i.appId,
      avatar: i.headImg,
      name: i.userName
    })),
    meetingInfo: {
      meetingNumber: result.meetingId,
      password: result.password,
      meetingTopic: result.topic
    }
  });
  return result;
}
export function handleCreateGroupMeeting() {
  return true;
}

export function transReceiveData(data: any) {
  let { body } = data;
  const { ext = {} } = body || data; // 应用的会邀和聊天的会邀结构不一致。应用的会邀没有body
  const {
    sessionState,
    meetingNumber,
    meetingPassword,
    meetingTopic,
    fromUser,
    toUsers = []
  } = ext;
  const receive = {
    fromUser,
    sessionState,
    meetingNumber,
    meetingTopic,
    toUsers,
    meetingPassword
  };
  return receive;
}

export function isSameUser(from: any, to: any) {
  if (from.userId && !from.pin) {
    from.pin = from.userId;
  }
  if (to.userId && !to.pin) {
    to.pin = to.userId;
  }
  return from && to && from.pin === to.pin && from.teamId === to.teamId && from.app === to.app;
}

export function leaveMeeting() {
  return new Promise((resolve) => {
    FocusSDK.once("zoom:leaveCb", resolve);
    FocusSDK.sendIpcMessage("zoom:leave");
  });
}

export function joinMeeting(
  meetingInfo: {
    password: string;
    meetingId: string;
    realName: string;
    meetingTopic?: string;
  },
  isvideooff = true,
  isaudiooff = true
) {
  return new Promise((resolve, reject) => {
    if (!meetingInfo) {
      reject(new Error("参数缺失"));
      return;
    }
    if (!isFocusEnv()) {
      reject(new Error("暂不支持"));
      return;
    }
    let messager: string | null = null;

    function sendLogin() {
      let timeout: any = setTimeout(() => {
        clearEventHandler();
      }, 30000);
      function clearEventHandler() {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        FocusSDK.off("zoom.meetingStatusCb", handleMeetingStatusCb);
      }
      function handleMeetingStatusCb(data: any) {
        if (messager) {
          message.destroy();
        }
        let msg = "";
        switch (data) {
          case ZoomMeetingStatus.MEETING_STATUS_IDLE:
            msg = "未初始化";
            break;
          case ZoomMeetingStatus.MEETING_STATUS_CONNECTING:
            break;
          case ZoomMeetingStatus.MEETING_STATUS_INMEETING:
            resolve(true);
            break;
          case ZoomMeetingStatus.MEETING_STATUS_FAILED:
            clearEventHandler();
            msg = i18n.t("common:joinMeeting.meeting connect failed");
            reject(new Error(msg));
            break;
          case ZoomMeetingStatus.MEETING_STATUS_ENDED:
            clearEventHandler();
            msg = i18n.t("common:joinMeeting.meeting has already ended");
            reject(new Error(msg));
            break;
          default:
            break;
        }
      }
      FocusSDK.off("zoom.meetingStatusCb", handleMeetingStatusCb).on(
        "zoom.meetingStatusCb",
        handleMeetingStatusCb
      );

      // 发送消息到桌面端
      FocusSDK.sendIpcMessage("zoom.joinUnlogin", {
        meetingnum: meetingInfo.meetingId,
        psw: meetingInfo.password,
        username: meetingInfo.realName,
        isvideooff,
        isaudiooff
      });
    }
    if (!cache.zoomLogin) {
      prepareZoom().then(({ success, errMsg }) => {
        if (!success) {
          reject(new Error(errMsg || "会议初始化失败"));
          return;
        }
        sendLogin();
      });
    } else {
      sendLogin();
    }
  });
}

export async function handleCreateSingleMeeting({
  from,
  to,
  type
}: {
  from: BasicUserInfo;
  to: BasicUserInfo;
  type?: string;
}) {
  if (!isFocusEnv()) {
    return Promise.reject(new Error("暂不支持网页端"));
  }
  if (from.pin === to.pin) {
    return Promise.reject(new Error("无法呼叫自己"));
  }
  if (!cache.zoomLogin) {
    const { success, errMsg } = await prepareZoom();
    if (!success) {
      return Promise.reject(new Error(errMsg || "会议初始化失败"));
    }
  }
  const zoomStatus = await getJoyMeetingStatus();
  if (zoomStatus !== JoyMeetingStatusEnum.None) {
    const confirm = await syncConfirmNew(
      "有会议在进行中，是否强制结束现有会议并加入新的会议？",
      "提示",
      {}
    );
    if (confirm) {
      await leaveMeeting();
    } else {
      return Promise.reject(new Error(i18n.t("joinMeeting.There is a meeting going on")));
    }
  }
  const [result, err] = await createMeeting({
    startTime: (new Date().getTime() + 5000).toString(),
    duration: 2,
    topic: i18n
      .t("chat:joyMeeting.meetingTopicDesc")
      .replace("%from", from.name)
      .replace("%to", to.name),
    password: "",
    participantIds: [
      {
        realName: from.name,
        teamId: from.teamId,
        userId: from.pin,
        appId: from.app,
        headImg: from.avatar
      },
      {
        realName: to.name,
        teamId: to.teamId,
        userId: to.pin,
        appId: to.app,
        headImg: to.avatar
      }
    ]
  });
  if (err || !result) {
    return Promise.reject(new Error(err || "会议初始化失败"));
  }

  bus.emit("meeting:create-single-call", {
    avsdk: AVSDK.ZOOM,
    toUser: to,
    meetingInfo: result,
    type
  });

  return result;
}

export async function getZoomStatus(): Promise<Boolean> {
  const status = await getJoyMeetingStatus();
  return status !== JoyMeetingStatusEnum.None;
}

export function resetZoomStatus() {
  FocusSDK.sendIpcMessage("MeetingControl:changeJoyMeetingStatus", JoyMeetingStatusEnum.None);
}

export function prepareZoom(): Promise<{ success: boolean; errMsg?: string }> {
  return new Promise((resolve) => {
    function handleInitCb(result: { status: number; code: string }) {
      const { status, code } = result;
      if (status !== 1) {
        resetZoomStatus();
        syncWarn(
          `请确认以下情况
  1.请查看网络连接情况
  2.请确认入会设备系统时间为当前时间
  3.请关闭其他会议软件
  4.请关闭杀毒软件
确认以上操作后，重新进入会议`,
          "提示",
          {
            className: "task-common-modal-lb align-left"
          }
        );
        if (status === -1) {
          // zoom SDK报错
        }
        let errMsg = i18n.t("joinMeeting.meeting initialization failure");

        if (code === "14") {
          errMsg = i18n.t("joinMeeting.The other instance of the SDK is in process");
        }
        resolve({
          success: false,
          errMsg
        });
        return;
      }
      resolve({
        success: true
      });
    }
    // 检查sdk状态
    FocusSDK.once("zoom.initCb", handleInitCb);
    FocusSDK.sendIpcMessage("zoom.initSdk");
  });
}

export function getJoyMeetingStatus(): Promise<JoyMeetingStatusEnum> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("time out"));
    }, 3000);
    FocusSDK.once("MeetingControl:getJoyMeetingStatusCb", ({ code }) => {
      clearTimeout(timeout);
      resolve(code);
    });
    FocusSDK.sendIpcMessage("MeetingControl:getJoyMeetingStatus");
  });
}
